import Axios, { AxiosResponse } from 'axios'
import {
  GetBroadCastParams,
  GetCurationDetailParams,
  GetCurationParams,
  GetScheduleParams,
  GetTopBannerParams,
  UserTokenInformation,
} from 'core/api/types'
import { getAge, getConfig, getGender, getMemberId, getPartnerId } from 'core/singleton'
import { reverseSplitArray, splitArray } from 'core/util/object'

import { currentDate } from 'core/store/broadcastTable'
import { MODULE_NAME } from 'core/constants'
import getBroadcastStatus from 'core/util/broadcastStatus'
import { getKinesis } from 'core/util/kinesis'
import { encode } from 'base64-url'

const prefix =
  process.env.REACT_APP_ENV === 'development'
    ? 'dev.'
    : process.env.REACT_APP_ENV === 'stage'
    ? 'stage.'
    : ''

export const clipPrefix =
  process.env.REACT_APP_ENV === 'development'
    ? 'development.'
    : process.env.REACT_APP_ENV === 'stage'
    ? 'stage.'
    : ''

export const GOOGLE_ANALYTICS_MEASUREMENT_ID = process.env.REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID

export const API_TIME_OUT = 3000
export const baseUrl = `https://${prefix}api.sauceflex.com/V1`
export const baseCacheURL = `https://cache-${prefix}api.sauceflex.com/V1`
// clip API cache 처리 되면 cache prefix 추가
export const clipBaseUrl = `https://${clipPrefix}api.sauceclip.com`

export const clipResourceUrl = `https://${clipPrefix}resources.sauceclip.com`

// Token 관련 API는 cache prefix 제거
export const tokenBaseURL =
  process.env.REACT_APP_ENV === 'development'
    ? 'https://dev.api.sauceflex.com/V1'
    : process.env.REACT_APP_ENV === 'stage'
    ? 'https://stage.api.sauceflex.com/V1'
    : 'https://api.sauceflex.com/V1'

const axios = Axios.create({
  baseURL: baseCacheURL,
})

const axiosWithTimeOut = Axios.create({
  baseURL: baseCacheURL,
  timeout: API_TIME_OUT,
})

const API = {
  /**
   * @description 상품 클릭 시 데이터 수집
   **/
  getTrackingProductUrl: async ({
    broadcastId,
    productId,
    roomId,
  }: {
    broadcastId: string
    productId: string
    roomId: string
  }) => {
    try {
      const { os, playStart, referrer, sessionId, userAgent, userBrowser, userHost, userUri } =
        getKinesis()
      const memberId = getMemberId()
      const partnerId = getPartnerId()
      const age = getAge()
      const gender = getGender()

      const eElk = encode(
        JSON.stringify({
          ageCode: age,
          genderCode: gender,
          isAdmin: false,
          referrer,
          roomId,
          sessionId,
          userAgent,
          userBrowser,
          userHost,
          userUri,
        }),
      )

      const res = await axiosWithTimeOut.get<API<{ redirectUrl: string }>>(
        `${baseUrl}/internal/analysis/product/click`,
        {
          params: {
            broadcastId,
            eElk,
            isRedirect: false,
            memberId,
            os,
            partnerId,
            playStart,
            productId,
          },
        },
      )

      return res.data.response
    } catch (e) {
      console.error('getTrackingProductUrl', e)
    }
  },
  /**
   * @description  토큰 유효성 검사 API
   **/
  getValidationToken: async (accessToken: string) => {
    const res = await Axios.get<API<TokenResponse>>(`${tokenBaseURL}/internal/token/validation`, {
      params: {
        accessToken,
        tokenType: 0,
      },
    })
    return res.data.response
  },
  /**
   * @description 게스트 토큰발급 API
   **/
  getGuestToken: async () => {
    const res = await Axios.post<API<TokenResponse>>(`${tokenBaseURL}/internal/token`, {
      age: 'etc',
      expiresIn: 315360000,
      gender: 'e',
      memberType: '0',
      nickName: `GUEST${Math.floor(Math.random() * Math.pow(10, 10))}`,
      partnerId: getPartnerId(),
      refreshExpiresIn: 3600,
    })
    return res.data.response
  },

  getCustomToken: async (arg: UserTokenInformation) => {
    const res = await Axios.post<API<TokenResponse>>(`${tokenBaseURL}/internal/token`, arg)
    return res.data.response
  },
  /**
   * @description 최신라이브 & VOD
   **/
  getBroadcast: async ({ keyword, nextKey, isSearch, limit }: GetBroadCastParams) => {
    let url = `/front/broadcast?partnerId=${getPartnerId()}&returnLimit=${limit}`
    if (nextKey !== 'init' && nextKey) {
      url += `&nextKey=${encodeURIComponent(nextKey)}`
    }
    if (isSearch) {
      if (keyword) {
        url += `&searchWord=${keyword}`
      } else {
        return
      }
    }

    const res = await axios.get<API<BroadcastResponse>>(url)
    return res.data.response
  },
  /**
   * @description 최신라이브 & VOD
   **/
  getBroadcastSearch: async ({
    keyword,
    nextKey,
    isSearch,
    limit,
    activeTab,
  }: GetBroadCastParams) => {
    let url = `/front/broadcast?partnerId=${getPartnerId()}&returnLimit=${limit}&searchType=2`

    if (activeTab) {
      // url += `&broadcastType=${broadcastType}`
    }

    if (nextKey !== 'init' && nextKey) {
      url += `&nextKey=${encodeURIComponent(nextKey)}`
    }

    if (isSearch) {
      if (keyword) {
        url += `&searchWord=${encodeURIComponent(keyword)}`
      } else {
        return
      }
    }

    const res = await axios.get<API<BroadcastResponse>>(url)
    return res.data.response
  },
  /**
   * @description 다음 라이브 (스케쥴)
   **/
  getSchedule: async ({ nextKey, limit }: GetScheduleParams) => {
    let url = `/front/broadcast/schedule?partnerId=${getPartnerId()}&returnLimit=${limit}&showProductDetail=false`
    if (nextKey !== 'init' && nextKey) {
      url += `&nextKey=${encodeURIComponent(nextKey)}`
    }
    const res = await axios.get<API<ScheduleResponse>>(url)
    return res.data.response
  },
  /**
   * @description 큐레이션 목록 모듈화 사용시에는 비호출
   **/
  getCuration: async ({ nextKey, moduleName }: GetCurationParams) => {
    if (moduleName) return
    let reqUrl = `/front/curation/${getPartnerId()}`
    if (nextKey) {
      reqUrl += `?nextKey=${encodeURIComponent(nextKey)}`
    }

    const res = await axios.get<API<CurationResponse>>(reqUrl)
    return res.data.response
  },
  /**
   * @description 큐레이션 상세 목록
   **/
  getCurationDetail: async ({
    curationId,
    nextKey,
    limit,
    moduleName,
  }: GetCurationDetailParams) => {
    const curationModules = [MODULE_NAME.CURATION]
    if (!curationId || (moduleName && !curationModules.includes(moduleName))) return
    let url = `/front/curation/${getPartnerId()}/${curationId}?returnLimit=${limit}`
    if (nextKey !== 'init' && nextKey) {
      url += `&nextKey=${encodeURIComponent(nextKey)}`
    }
    const res = await axios.get<API<CurationDetailResponse>>(url)
    return res.data.response
  },
  /*
   * @description 상단 배너 목록
   **/
  getTopBanner: async ({ orderType }: GetTopBannerParams) => {
    if (orderType === undefined) return
    const now = new Date()
    const hours = now.getHours()
    const partnerId = getPartnerId()
    const url = `/front/topb/${partnerId}?orderType=${orderType}&key=${hours}`

    const res = await axios.get<API<GetTopBannerResponse>>(url)
    return res.data.response
  },
  /**
   * @description 상단 배너 클릭 카운트 수집
   **/
  patchTopBannerCount: async (topBannerId: string) => {
    if (!topBannerId) return
    const partnerId = getPartnerId()
    const url = `/front/topb/${partnerId}/${topBannerId}/count`

    await axios.patch(url)
    return
  },
  /**
   * @description 콘텐츠 배너 목록
   */
  getContentBanner: async ({ orderType }: GetTopBannerParams) => {
    if (orderType === undefined) return
    const partnerId = getPartnerId()
    const url = `${tokenBaseURL}/front/banner/content/${partnerId}?key=1`

    const res = await Axios.get<API<GetContentBannerResponse>>(url)
    return res.data.response
  },
  /**
   * @description 콘텐츠 배너 클릭 카운트 수집
   **/
  patchContentBannerCount: async (contentBannerId: string) => {
    if (!contentBannerId) return
    const partnerId = getPartnerId()
    const url = `/front/banner/content/${partnerId}/${contentBannerId}/count`

    await axios.patch(url)
    return
  },
  /**
   * @description 텍스트 배너 목록
   */
  getTextBanner: async ({ orderType }: GetTopBannerParams) => {
    if (orderType === undefined) return
    const partnerId = getPartnerId()
    const url = `${tokenBaseURL}/front/banner/text/${partnerId}?key=1`

    const res = await axios.get<API<GetTextBannerResponse>>(url)
    return res.data.response
  },
  /**
   * @description 콘텐츠 배너 클릭 카운트 수집
   **/
  patchTextBannerCount: async (textBannerId: string) => {
    if (!textBannerId) return
    const partnerId = getPartnerId()
    const url = `/front/banner/text/${partnerId}/${textBannerId}/count`

    await axios.patch(url)
    return
  },
  /**
   * @description 방송 알림 신청
   **/
  postAlarmData: async ({
    memberName,
    phone,
    broadcastId,
  }: {
    memberName?: string
    phone?: number
    broadcastId?: string
  }) => {
    const memberId = getMemberId()
    const partnerId = getPartnerId()

    const url = `internal/member/${memberId}/broadcast/notification?partnerId=${partnerId}&broadcastId=${broadcastId}${
      memberName ? `&memberName=${memberName}&phone=${phone}` : ''
    }`
    await axios.post(url)
    return
  },
  /**
   * @description 큐레이션 소스 클립
   **/
  getClipsData: async (page: number) => {
    const { sauceclip } = getConfig()
    if (!sauceclip?.isSauceClip) return undefined
    const partnerId = getPartnerId()
    const url = `${clipBaseUrl}/v2/display/partner/${partnerId}/clips`
    try {
      const response = await axios.get<AxiosResponse<ClipListResponse>>(url, {
        params: { limit: 10, page },
      })
      return response.data?.data
    } catch (error) {
      console.log(error)
      return undefined
    }
  },
  /**
   * @description 일자 별 라이브 개수 불러오기
   */
  getBroadcastCount: async (dateList: string[]) => {
    const partnerId = getPartnerId()
    try {
      const { data } = await axios.get<API<Record<string, number>>>(`/front/broadcast/exists`, {
        params: { partnerId, dates: dateList },
      })
      return data.response
    } catch (e) {
      console.log(e)
    }
  },
  /**
   * @description 이전 일자 / 다음 일자 라이브 편성표 목록 불러오기
   */ getPrevNextBroadcastList: async (date: Date) => {
    try {
      const searchStartDt = new Date(date.valueOf())
      const searchEndDt = new Date(date.valueOf())

      /**
       * 00:00:00 부터 23:59:59 까지 검색
       */
      searchStartDt.setHours(0, 0, 0, 0)
      searchEndDt.setHours(23, 59, 59, 59)

      const { data } = await axios.get<API<BroadcastResponse>>(
        `/front/broadcast?partnerId=${getPartnerId()}`,
        {
          params: {
            searchStartDt: searchStartDt.toISOString(),
            searchEndDt: searchEndDt.toISOString(),
            returnLimit: 999,
            /**
             * 라이브 예정 상태의 방송 포함
             */
            isIncludeFuture: true,
          },
        },
      )

      const broadcastList = [...data.response.items]

      /**
       * programmingStartDt 를 기준으로 오름차순 정렬
       */
      broadcastList.sort((prev, next) => {
        const prevStartDt = Date.parse(prev.programmingStartDt)
        const nextStartDt = Date.parse(next.programmingStartDt)
        return prevStartDt - nextStartDt
      })

      /**
       * 방송은 한 번에 4개씩 출력되어야하므로 배열을 4개씩 쪼개기
       */
      const splittedBroadcastList = splitArray(broadcastList, 4) as BroadcastItem[][]

      /**
       * 이전 / 다음 일자의 방송은 fromNow만 존재
       *
       * - 이전 / 다음 일자의 방송은 현재 시간과 상관 없이 첫 방송부터 출력
       *
       * - 이전 / 다음 일자의 방송은 "지난 라이브 열기"가 없고 "더보기"만 존재
       */
      const response: BroadcastListResponse = {
        ...data.response,
        items: {
          past: [],
          fromNow: splittedBroadcastList,
        },
      }

      return response
    } catch (e) {
      console.log(e)
    }
  },
  /**
   * @description 오늘 라이브 편성표 목록 불러오기
   */
  getTodayBroadcastList: async () => {
    try {
      const searchStartDt = new Date(currentDate.valueOf())
      const searchEndDt = new Date(currentDate.valueOf())

      /**
       * 00:00:00 부터 23:59:59 까지 검색
       */
      searchStartDt.setHours(0, 0, 0, 0)
      searchEndDt.setHours(23, 59, 59, 999)

      const { data } = await axios.get<API<BroadcastResponse>>(
        `/front/broadcast?partnerId=${getPartnerId()}`,
        {
          params: {
            searchStartDt: searchStartDt.toISOString(),
            searchEndDt: searchEndDt.toISOString(),
            returnLimit: 999,
            /**
             * 라이브 예정 상태의 방송 포함
             */
            isIncludeFuture: true,
          },
        },
      )

      const broadcastList = [...data.response.items]

      /**
       * 더보기 버튼 안으로 들어갈, 이미 끝난 방송들을 programmingStartDt 로 정렬한 리스트
       */
      let pastList = broadcastList.filter((item) => {
        const { isBroadcastEnd } = getBroadcastStatus(item.broadcastStateCode)
        return item.broadcastStateCode && isBroadcastEnd
      })

      pastList.sort((prev, next) => {
        const prevStartDt = Date.parse(prev.programmingStartDt)
        const nextStartDt = Date.parse(next.programmingStartDt)
        return prevStartDt - nextStartDt
      })

      /**
       * 더보기 버튼 안으로 들어가면 안되는, 아직 시작하지 않았거나 송출 중인 방송들을 programmingStartDt 로 정렬한 리스트
       */
      let fromNowList = broadcastList.filter((item) => {
        const { isBeforeBroadcast, isOnAir } = getBroadcastStatus(item.broadcastStateCode)
        const isBeforeOrOnAir = isBeforeBroadcast || isOnAir
        return item.broadcastStateCode && isBeforeOrOnAir
      })

      fromNowList.sort((prev, next) => {
        const prevStartDt = Date.parse(prev.programmingStartDt)
        const nextStartDt = Date.parse(next.programmingStartDt)
        return prevStartDt - nextStartDt
      })

      /**
       * 만일 아직 시작하지 않은 방송이 없고(FromNowList.length = 0) 끝난 방송이 있을 경우(PrevList.length > 0)
       * 모든 지나간 방송이 펼쳐져서 보이게하기
       */
      if (!fromNowList?.length && pastList.length > 0) {
        fromNowList = pastList
        pastList = []
      }

      /**
       * 방송은 한 번에 4개씩 출력되어야하므로 배열을 4개씩 쪼개기
       */
      const splittedPastList = reverseSplitArray(pastList, 4) as BroadcastItem[][]
      const splittedFromNowList = splitArray(fromNowList, 4) as BroadcastItem[][]

      /**
       * 오늘 일자의 방송은 현재 시간을 기준으로 지난 라이브 (prev)와 더보기 (fromNow)가 존재
       *
       * - 지난 라이브 열기 시 prev가 출력 (with prevListIndex)
       * - 더보기 시 fromNow가 확장되어 출력 (with moreListIndex)
       */
      const response: BroadcastListResponse = {
        ...data.response,
        items: {
          past: splittedPastList,
          fromNow: splittedFromNowList,
        },
      }

      return response
    } catch (e) {
      console.log(e)
    }
  },
}

export default API
